import React from "react";
import { Outlet } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { PageLoader } from "app/components/page-loader";
import { CanonicalUrl } from "app/components/canonical-url";
import { useStoreActions, useStoreState } from "app/state/store/hooks";
import { AccountProfileLayout } from "app/pages/account-profile/components/layout";

const AccountProfile: React.FC = () => {
  const { user, isAuthenticated, isLoading, getAccessTokenSilently } =
    useAuth0();

  const loading = useStoreState((state) => state.user.loading);
  const userDB = useStoreState((state) => state.user.data) as any;
  const rolesLoading = useStoreState((state) => state.roles.loading);
  const groupMembersLoading = useStoreState(
    (state) => state.groupMembers.loading
  );
  const subscriptionPlanLoading = useStoreState(
    (state) => state.StripeSubscriptionPlan.loading
  );

  const fetchRoles = useStoreActions((actions) => actions.roles.authGetFetch);
  const fetchUserAction = useStoreActions(
    (actions) => actions.user.authGetFetch
  );
  const fetchGroupMembers = useStoreActions(
    (actions) => actions.groupMembers.authGetFetch
  );
  const fetchUserInvitations = useStoreActions(
    (actions) => actions.userInvitations.authGetFetch
  );

  const getUser = () => {
    if (isAuthenticated && !isLoading && user) {
      getAccessTokenSilently().then((token: string) => {
        fetchUserAction({
          values: {
            token,
            q: user.sub,
          },
        });
      });
    }
  };

  const getRolesAndMembers = () => {
    if (isAuthenticated && !isLoading && user) {
      getAccessTokenSilently().then((token: string) => {
        fetchRoles({
          values: {
            token,
          },
        });
        if (userDB?.data.groupId) {
          fetchGroupMembers({
            values: { token },
            routeParams: { id: userDB?.data.groupId },
          });
          fetchUserInvitations({
            values: { token },
            dontEncryptAuthCall: true,
            filterString: `filter={"where":{"and":[{"groupId":"${userDB?.data.groupId}"},{"pending":"true"}]}}`,
          });
        }
      });
    }
  };

  React.useEffect(() => {
    getUser();
    getRolesAndMembers();
  }, [isAuthenticated, isLoading]);

  const combinedLoading = React.useMemo(() => {
    return (
      loading ||
      isLoading ||
      rolesLoading ||
      groupMembersLoading ||
      subscriptionPlanLoading
    );
  }, [
    loading,
    isLoading,
    rolesLoading,
    groupMembersLoading,
    subscriptionPlanLoading,
  ]);

  return (
    <React.Fragment>
      {combinedLoading && <PageLoader />}
      <CanonicalUrl canonicalUrl="/account-profile" />
      <AccountProfileLayout>
        <Outlet />
      </AccountProfileLayout>
    </React.Fragment>
  );
};

export default AccountProfile;
